import { trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import { useContainerRef } from '@autone/utils';
import { Download, EllipsisVertical, Upload } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { parse } from 'papaparse';
import { useState } from 'react';
import { CSVLink } from 'react-csv';

import { useLazyValidateProductIdsQuery } from '../../redux/services';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TailwindButton,
} from '../../tailwind';
import { formats, getFile } from '../../utils';

import { PRODUCT_DIMENSION_NAME } from './consts';
import { type ProductUploadPopoverProps } from './ProductUploadPopover.types';
import { extractProductsFromCsvData, getParsingError, readFile } from './utils';

export const ProductUploadPopover = ({
  onValidUpload,
  analyticsEvents,
}: ProductUploadPopoverProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { common } = useAutoneTranslation();

  const { uploadAnalyticsEvent, downloadAnalyticsEvent } = analyticsEvents;
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [trigger, { isLoading: isValidationLoading }] =
    useLazyValidateProductIdsQuery();

  const handleError = (errorMessage: string) => {
    enqueueSnackbar(errorMessage, { variant: 'error' });
    trackAnalyticsEvent({ ...uploadAnalyticsEvent.error });
  };

  const handleOnUploadExportTagFile = async () => {
    setIsUploading(true);
    trackAnalyticsEvent({ ...uploadAnalyticsEvent.clicked });

    try {
      const file = await getFile(formats.ONLY_CSV);
      if (!file) {
        return;
      }

      const fileData = await readFile(file);
      const { data, errors } = parse(fileData, {
        header: true,
        delimiter: ',',
      });

      if (errors.length > 0) {
        const errorMessage = getParsingError(common, errors);
        handleError(errorMessage);
        return;
      }

      const productData = extractProductsFromCsvData(data);
      if (!productData) {
        return;
      }

      const validationResult = await trigger({
        productIds: productData.map(({ id }) => id),
      }).unwrap();

      const invalidProductIds = new Set(
        validationResult.map(({ product_id: productId }) => productId),
      );
      const validProducts = productData.filter(
        ({ id }) => !invalidProductIds.has(id),
      );

      if (validProducts.length === 0) {
        const errorMessage = common(
          'error-messages.csv-upload.invalid-products',
        );
        handleError(errorMessage);
        return;
      }

      if (validationResult.length > 0) {
        enqueueSnackbar(
          common('error-messages.csv-upload.invalid-products-removed'),
          { variant: 'warning' },
        );
      }

      onValidUpload(validProducts);
      trackAnalyticsEvent({ ...uploadAnalyticsEvent.success });
    } catch (_) {
      handleError(common('error-messages.csv-upload.default'));
    } finally {
      setIsUploading(false);
    }
  };

  const dropdownTriggerRef = useContainerRef<HTMLButtonElement>(null);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild ref={dropdownTriggerRef}>
        <TailwindButton
          variant="ghost"
          color="secondary"
          size="sm"
          className="p-0 h-6 w-6"
        >
          <EllipsisVertical size={15} />
        </TailwindButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-fit"
        align="end"
        container={dropdownTriggerRef.current as unknown as HTMLDivElement}
      >
        <CSVLink
          data={[[PRODUCT_DIMENSION_NAME]]}
          filename="product_import.csv"
          target="_blank"
          style={{ textDecoration: 'none', color: 'inherit' }}
          onClick={() => {
            trackAnalyticsEvent({ ...downloadAnalyticsEvent.clicked });
          }}
        >
          <DropdownMenuItem className="gap-2 justify-start">
            <Download className="h-4 w-4" />
            {common('download-template')}
          </DropdownMenuItem>
        </CSVLink>
        <DropdownMenuItem
          onSelect={handleOnUploadExportTagFile}
          className="gap-2 justify-start"
          disabled={isUploading || isValidationLoading}
        >
          <Upload className="h-4 w-4" />
          {common('upload-template')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
